import * as _ from 'lodash';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import Rollbar from 'rollbar';
import { Component, Input, Inject, OnInit } from '@angular/core';
import { RollbarService } from 'app/rollbar';

@Component({
  selector: 'activity-row-default',
  templateUrl: 'activity-row-default.component.html',
  styleUrls: ['activity-row-default.component.scss']
})
export class ActivityRowDefaultComponent implements OnInit {
  @Input() activity;
  activityIcon = 'icon-20-stage-revive';
  updatedActivity: {
    icon: string;
    title: string;
    content: string;
    link: {
      href: string;
      tooltip: string;
    };
    diff: string;
  };
  showDiff = false;

  constructor(
    @Inject(RollbarService) private rollbar: Rollbar,
    private localSettingService: LocaleSettingService,
  ) { }

  ngOnInit() {
    const localeCurrencySymbol = this.localSettingService.getLocaleCurrencySymbol();

    this.updatedActivity = Object.assign(
      {},
      this.activity,
      getDefaultsForActivity(this.activity, this.rollbar, localeCurrencySymbol),
      { original: undefined } // Temporarily squelch this to make jsonified activity easier to read while devving
    );
  }
}

const getDefaultsForActivity = (activity, rollbar, localeCurrencySymbol) => {
  let title, icon, content, link;
  try {
    title = getTitleForActivity(activity);
  } catch (e) {
    rollbar.error('Activities tab: failed to calculate title for activity', { activity: activity, err: e });
  }

  try {
    icon = getIconForActivity(activity);
  } catch (e) {
    rollbar.error('Activities tab: failed to calculate icon for activity', { activity: activity, err: e });
  }

  try {
    content = getContentForActivity(activity, localeCurrencySymbol);
  } catch (e) {
    rollbar.error('Activities tab: failed to calculate content for activity', { activity: activity, err: e });
  }

  try {
    link = getLinkForActivity(activity);
  } catch (e) {
    rollbar.error('Activities tab: failed to calculate link for activity', { activity: activity, err: e });
  }

  return { title, icon, content, link };
};

const getTitleForActivity = (activity) => {
  switch (activity.type) {
    case 'acceptedBankStatementsTerms': return 'Connecting to Finicity';
    case 'appointment': return 'Appointment Scheduled';
    case 'appointment-fired': return 'Appointment';
    case 'application': return 'Application in Progress';
    case 'applicationCreated': return 'Package Sent';
    case 'assignment': return 'New Assignment';
    case 'bouncedMessage': return 'Undelivered Message';
    case 'call':
      if (!activity || !activity.callType) {
        return 'Call';
      }
      return _.startCase(activity.callType) + ' Phone Call';
    case 'chat':
      if (!activity || !activity.subType) {
        return 'Chat Message';
      }
      return _.startCase(activity.subType) + ' Chat Message';
    case 'cxInteraction': return 'CX Interaction';
    case 'created': return 'Lead Created';
    case 'deal': return 'Deal Funded';
    case 'dealCreated': return 'Deal Started';
    case 'dealUpdated': return 'Deal Updated';
    case 'dealPassed': return 'Deal Sent to Marketplace';
    case 'deleted':
      switch (activity.deletedType) {
        case 'note': return 'Note Deleted';
        case 'document': return 'Document Deleted';
        case 'reminder': return 'Reminder Deleted';
        case 'message': return 'Message Deleted';
        case 'call': return 'Call Cancelled';
        default: return 'Deleted';
      }
    case 'document': return 'Document Uploaded';
    case 'lenderDocument': return 'Document Uploaded';
    case 'documentAttached': return 'Documents Updated';
    case 'documentRemoved': return 'Documents Removed';
    case 'docusignEnvelopeCreated': return 'Docusign Envelope Created';
    case 'docusignEnvelopeUpdated': return 'Docusign Envelope Updated';
    case 'edited': return 'Information Updated';
    case 'deletedLoan': return 'Outstanding Loan Deleted';
    case 'lenderUpdatedDeal': return 'Lender Updated Deal';
    case 'message': return 'Message Sent';
    case 'note': return 'Note Created';
    case 'ocrolusFailed': return 'Bank Statement Import Failed';
    case 'ocrolusSubmitted': return 'Bank Statements Submitted to Ocrolus';
    case 'ocrolusSucceeded': return 'Bank Statement Import Finished';
    case 'ocrolusFailedToCreateBook': return 'Ocrolus Failed to Create Book';
    case 'offerCreated': return 'Offer Received';
    case 'offerAccepted': return 'Offer Accepted';
    case 'offerDeleted': return 'Offer Removed';
    case 'offerUpdated': return 'Offer Updated';
    case 'offerRejected': return 'Offer Rejected';
    case 'reactivated': return 'Business Reactivated';
    case 'reminder': return 'Reminder Scheduled';
    case 'reminder-fired': return 'Follow Up Reminder';
    case 'requestCompleted': return 'Stipulations Collected';
    case 'requestCreated': return 'Stipulations Needed';
    case 'requestDeleted': return 'Stipulations Removed';
    case 'requestRejected': return 'Stipulations Rejected';
    case 'requestAccepted': return 'Stipulations Accepted';
    case 'requestPublished': return 'Stipulations Sent';
    case 'requestDelivered': return 'Stipulations Delivered';
    case 'reward': return 'Gift Sent';
    case 'sentToLendio': return 'Sent to Lendio';
    case 'shared': return 'Business Shared';
    case 'sms':
      if (!activity || !activity.subType) {
        return 'Text Message';
      }
      return _.startCase(activity.subType) + ' Text Message';
    case 'status': return 'Status Change';
    case 'unshared': return 'Sharing Removed';
    case 'offerPublishedToBorrower': return 'Offer Sent to Borrower Portal';
    case 'offerBorrowerAccepted': return 'Offer Accepted via Borrower Portal';
    case 'offerBorrowerDeclined': return 'Offer Declined via Borrower Portal';
    case 'veritaxProcessStarted': return 'Getting Tax Transcripts';
    case 'softPullCreditCheck': return 'Soft Pull Credit Check';
    case 'documentNameChange': return 'Document Name Change';
    default: return _.capitalize(_.snakeCase(activity.type).replace('_', ' '));
  }
};

const getIconForActivity = (activity) => {
  switch (activity.type) {
    case 'application': return 'description';
    case 'assignment': return 'person';
    case 'bouncedMessage': return 'email';
    case 'call':
      if (!activity || !activity.callType) {
        return 'phone_in_talk';
      }
      return 'phone_in_talk';
    case 'chat':
      if (!activity || !activity.subType) {
        return 'chat';
      }
      return 'chat';
    case 'cxInteraction': return 'label_outline';
    case 'created':
      return 'new_releases';
    case 'deleted':
      if (!activity || !activity.deletedType) {
        return 'delete';
      }
      return 'delete';
    case 'edited': return 'edit';
    case 'deletedLoan': return 'delete';
    case 'message': return 'email';
    case 'deal': return 'attach_money';
    case 'reward': return 'card_giftcard';
    case 'sentToLendio': return 'send';
    case 'shared': return 'folder_shared';
    case 'sms':
      if (!activity || !activity.subType) {
        return 'message';
      }
      return 'message';
    case 'unshared':
      return 'notifications';
    case 'status':
      if (!activity || !activity.stage) {
        return 'keyboard_tab';
      }
      return 'keyboard_tab';
    case 'applicationCreated': return 'north_east';
    case 'dealCreated': return 'local_offer';
    case 'dealUpdated': return 'local_offer';
    case 'dealPassed': return 'local_offer';
    case 'document': return 'note_add';
    case 'documentRemoved': return 'remove_circle_outline';
    case 'acceptedBankStatementsTerms': return 'account_balance';
    case 'ocrolusFailed': return 'remove_circle_outline';
    case 'ocrolusSubmitted': return 'note_add';
    case 'ocrolusSucceeded': return 'note_add';
    case 'ocrolusFailedToCreateBook': return 'remove_circle_outline';
    case 'offerCreated': return 'south_west';
    case 'offerAccepted': return 'local_offer';
    case 'offerDeleted': return 'remove_circle_outline';
    case 'offerUpdated': return 'note_add';
    case 'offerRejected': return 'remove_circle_outline';
    case 'note': return 'notes';
    case 'reminder': return 'schedule';
    case 'reminder-fired': return 'schedule';
    case 'requestCompleted': return 'note_add';
    case 'requestCreated': return 'note_add';
    case 'requestDeleted': return 'remove_circle_outline';
    case 'requestRejected': return 'remove_circle_outline';
    case 'requestAccepted': return 'note_add';
    case 'requestPublished': return 'note_add';
    case 'requestDelivered': return 'note_add';
    case 'offerPublishedToBorrower': return 'north_east';
    case 'offerBorrowerAccepted': return 'done';
    case 'offerBorrowerDeclined': return 'not_interested';
    case 'reactivated': return 'new_releases';
    case 'veritaxProcessStarted': return 'account_balance';
    case 'softPullCreditCheck': return 'account_balance';
    default: return 'restore';
  }
};

const getClassForStage = (stage: string) => {
  switch (stage) {
    case 'fund':
      return 'funded';
    case 'work':
      return 'application';
    case 'contact':
      return 'contacting';
    case 'grow':
      return 'closing';
    default:
      return stage;
  }
};

const getContentForActivity = (activity, localeCurrencySymbol) => {
  const lenderName = activity.meta.loanProductLenderName || (activity.meta.loanProductLender ? activity.meta.loanProductLender.name : null);
  const productName = lenderName + (activity.meta.productName ? ' ' + activity.meta.productName : '');
  switch (activity.type) {
    case 'acceptedBankStatementsTerms':
      return (
        (activity.primaryContactName || activity.borrowerName) +
        ' just started the process of connecting to a bank and accepted the terms.'
      );
    case 'applicationCreated':
      return activity.meta.lenderUserName + ' sent a deal to (' + productName + ')';
    case 'dealCreated':
      return (
        (activity.meta.createdByUserName || activity.meta.lenderUserName) +
        ' just started a new deal for ' + activity.borrowerName +
        ' (' + lenderName + ' ' + activity.meta.name + ')'
      );
    case 'dealUpdated':
      return (
        activity.meta.lenderUserName + ' updated deal owner from ' +
        activity.meta.dealOldOwner + ' to ' + activity.meta.dealNewOwner +
        ' (' + productName + ')'
      );
    case 'dealPassed':
      return (
        (activity.meta.lenderUserName) + 'passed ownership and sent this to the marketplace!' +
        ' (' + productName + ')'
      );
    case 'document':
    case 'lenderDocument':
      return 'by ' + (activity.meta.category === 'creditReport'
        ? 'System Account'
        : activity.meta.uploadedBy === 'borrower'
          ? activity.meta.primaryContactName || activity.borrowerName
          : activity.meta.lenderUserName
      );
    case 'documentAttached':
      return (
        activity.meta.lenderUserName +
        ' just added additional documents to the deal for ' +
        activity.borrowerName +
        ' (' + productName + ')'
      );
    case 'documentRemoved':
      return activity.meta.lenderUserName + ' just removed documents from the deal for ' + activity.borrowerName + ' (' + productName + ')';
    case 'docusignEnvelopeCreated':
      return activity.meta.lenderUserName + ' created and sent a new Docusign Envelope.';
    case 'docusignEnvelopeUpdated':
      return activity.meta.lenderUserName + ' updated the current Docusign envelope status to ' + activity.meta.reason + '.';
    case 'deletedLoan':
      // eslint-disable-next-line max-len
      return '(' + (productName || activity.meta.lenderName) + (activity.meta.amount ? `, ${localeCurrencySymbol}` + activity.meta.amount : '') + ')';
    case 'ocrolusFailed':
      return activity.meta.message;
    case 'ocrolusSubmitted':
      return activity.meta.documentsAdded + ' statements added, ' + activity.meta.documentsRemoved + ' statements removed';
    case 'offerCreated':
      return 'from ' + (activity.meta.contactLenderUserName
        ? (activity.meta.contactLenderUserName + ' (' + productName + ')')
        : (productName));
    case 'offerAccepted':
      return activity.meta.lenderUserName + ' marked an offer as accepted for ' + activity.borrowerName + ' (' + productName + ')';
    case 'offerPublishedToBorrower':
      return activity.meta.lenderUserName + ' sent an offer to ' + activity.borrowerName + ' (' + productName + ')';
    case 'offerBorrowerAccepted':
      return activity.meta.borrowerUserName + ' accepted an offer for ' + activity.borrowerName + ' (' + productName + ')';
    case 'offerBorrowerDeclined':
      return activity.meta.borrowerUserName + ' declined an offer for ' + activity.borrowerName + ' (' + productName + ')';
    case 'offerDeleted':
      return activity.meta.lenderUserName + ' removed an offer for ' + activity.borrowerName + ' (' + productName + ')';
    case 'offerUpdated':
      return activity.meta.lenderUserName + ' updated an offer for ' + activity.borrowerName + ' (' + productName + ')';
    case 'reactivated':
      return (
        (activity.meta.affiliateName || activity.meta.initiatorName || activity.meta.lenderUserName) +
        ' has reactivated ' + activity.borrowerName
      );
    case 'reward':
      return (
        activity.meta.borrowerUserName + ' received a ' +
        (activity.meta.amount ? activity.meta.amount + ' ' : '') +
        'gift card for the Borrower Referral Program'
      );
    case 'status':
      return (
        (activity.dealId ? 'Deal' : 'Business') +
        ' moved from "' + _.startCase(activity.oldStatus) +
        '" to "' + _.startCase(activity.status) +
        '" by ' + activity.meta.lenderUserName
      );
    case 'veritaxProcessStarted':
      return (
        (activity.meta.primaryContactName || activity.borrowerName) +
        ' just started the process of getting tax transcripts from Veri-Tax'
      );
    case 'requestCreated':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' added requests for stipulations ' +
        (activity.meta.dealName ? 'to ' + activity.meta.dealName : '')
      );
    case 'requestDeleted':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' removed requests for stipulations ' +
        (activity.meta.dealName ? 'from ' + activity.meta.dealName : '')
      );
    case 'requestCompleted':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' completed stipulations ' +
        (activity.meta.dealName ? 'for ' + activity.meta.dealName : '')
      );
    case 'requestAccepted':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' marked stipulations as accepted ' +
        (activity.meta.dealName ? 'for ' + activity.meta.dealName : '')
      );
    case 'requestPublished':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' sent request for stipulations to borrower ' +
        (activity.meta.dealName ? 'for ' + activity.meta.dealName : '')
      );
    case 'requestDelivered':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' sent completed requests for stipulations to lender ' +
        (activity.meta.dealName ? 'for ' + activity.meta.dealName : '')
      );
    case 'requestRejected':
      return (
        (activity.meta.updatedByName || activity.meta.lenderUserName) + ' rejected stipulations to lender ' +
        (activity.meta.dealName ? 'for ' + activity.meta.dealName : '')
      );
    case 'softPullCreditCheck':
      return 'done by System Account';
    case 'shared':
      return (
        activity.meta.lenderUserName + ' shared ' + activity.borrowerName + ' with ' +
        (Object.values(activity.meta.sharedWithNames)).join(', ')
      );
    case 'unshared':
      return (
        activity.meta.lenderUserName + ' stopped sharing ' + activity.borrowerName + ' with ' +
        (Object.values(activity.meta.sharedWithNames)).join(', ')
      );
    case 'lenderUpdatedDeal':
      return (
        activity.meta.institutionName + ' updated ' + activity.meta.loanProductName + ' status'
      );
    case 'sentToLendio':
      return (
        activity.meta.lenderUserName + ' sent ' + activity.borrowerName + ' to Lendio'
      );
  }
};

interface LinkDetails {
  title: string;
  tooltip?: string;
  href?: string;
}
const getLinkForActivity = (activity): LinkDetails | undefined => {
  switch (activity.type) {
    case 'requestCreated': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
    case 'requestDeleted': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
    case 'requestCompleted': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
    case 'requestAccepted': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
    case 'requestPublished': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
    case 'requestDelivered': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
    case 'requestRejected': return { title: 'See stipulations', tooltip: activity.meta.requestNames };
  }
};
